import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useCreatedDistricts } from "@/api/reseller";
import type { CreatedDistrict } from "@/api/reseller/types";
import type { Column } from "@/components/meters/meter-table";
import { MeterTable, usePagination } from "@/components/meters/meter-table";
import { RowDetailsButton } from "@/components/table";
import { Button } from "@/components/ui/button";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { CreateDistrictDialog } from "./create-district-dialog";

function formatDateTime(value: string, locale: string): string {
	return new Date(value).toLocaleString(locale, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function DistrictsTableReseller() {
	const { t, i18n } = useTranslation();
	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [search, setSearch] = useState("");
	const [sheetOpen, setSheetOpen] = useState(false);
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";

	const [ordering, setOrdering] = useState("");
	const { data, isLoading } = useCreatedDistricts({
		page,
		page_size: pageSize,
		search: search || undefined,
		ordering: ordering || undefined,
	});

	const columns: Column<CreatedDistrict>[] = [
		{ key: "name", label: t("districts.colName"), sortField: "name" },
		{
			key: "manager",
			label: t("districts.colManager"),
			render: (row) => row.manager.company_name,
		},
		{
			key: "meters_count",
			label: t("districts.colMeters"),
			render: (row) => row.meters_count,
		},
		{
			key: "id",
			label: t("districts.colManagerCreatedAt"),
			render: (row) => formatDateTime(row.manager.created_at, i18n.language),
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<RowDetailsButton
					onClick={() =>
						navigate(
							`/${currentLang}/${ROUTE_SLUGS.districts[currentLang]}/${encodeURIComponent(row.name)}?id=${row.id}&meters=${row.meters_count}`,
						)
					}
				/>
			),
		},
	];

	return (
		<>
			<MeterTable
				data={data}
				columns={columns}
				loading={isLoading}
				page={page}
				pageSize={pageSize}
				onPageChange={(p) => setPage(p)}
				onPageSizeChange={(s) => {
					setPageSize(s);
					setPage(1);
				}}
				search={search}
				onSearchChange={(s) => {
					setSearch(s);
					setPage(1);
				}}
				searchPlaceholder={t("districts.searchPlaceholder")}
				ordering={ordering}
				onOrderingChange={(o) => {
					setOrdering(o);
					setPage(1);
				}}
				headerActions={
					<Button onClick={() => setSheetOpen(true)}>
						<PlusIcon />
						{t("districts.createButton")}
					</Button>
				}
			/>
			<CreateDistrictDialog
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
			/>
		</>
	);
}
